// client/src/pages/ErrorResolution.tsx
//
// Error Resolution Agent — per-target cards with the AI resolution brief shown
// prominently (markdown), severity + review status, and (for logic apps) the
// live Logic App Health stats joined in. Admins can run the digest on demand
// and advance the review status.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Wrench, RefreshCw, ExternalLink, Clock, Search, Play, AlertTriangle,
  CheckCircle2, Activity, Timer, Mail,
} from 'lucide-react';

type Resolution = {
  resolutionId: number;
  digestDate: string;
  targetType: 'swa' | 'logicapp';
  targetName: string;
  contextApp?: string;
  severity?: 'critical' | 'high' | 'medium' | 'low' | string;
  title?: string;
  summaryMd?: string;
  errorCount?: number;
  sources?: string[];
  firstSeen?: string | null;
  lastSeen?: string | null;
  portalLink?: string | null;
  modelUsed?: string;
  estimatedCost?: number;
  status?: string;
};
type Overview = { generatedAt: string; days: number; totals: Record<string, number>; resolutions: Resolution[] };

type HealthApp = {
  name: string; state?: string; runCount?: number; failed?: number;
  maxDurationSec?: number | null; schedule?: string | null;
  lastRun?: { status: string; startTime: string } | null;
};

const SEV_ORDER = ['critical', 'high', 'medium', 'low'];
function sevColor(s?: string): string {
  switch (s) {
    case 'critical': return '#ff4d4f';
    case 'high': return 'var(--bad)';
    case 'medium': return '#fbbf24';
    case 'low': return '#9ca3af';
    default: return 'var(--muted)';
  }
}
function fmtDur(sec?: number | null): string {
  if (sec == null) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) { const m = Math.floor(sec / 60); const s = Math.round(sec % 60); return s ? `${m}m ${s}s` : `${m}m`; }
  const h = Math.floor(sec / 3600); const m = Math.round((sec % 3600) / 60); return m ? `${h}h ${m}m` : `${h}h`;
}
function fmtWhen(iso?: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso); if (!t) return '—';
  const d = (Date.now() - t) / 1000;
  if (d < 3600) return `${Math.max(1, Math.floor(d / 60))}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}
async function getJson<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, { credentials: 'include', ...init });
  if (!r.ok) { let m = `HTTP ${r.status}`; try { const b = await r.json(); if (b?.error) m = b.error; } catch {} throw new Error(m); }
  return r.json();
}

function SeverityPill({ s }: { s?: string }) {
  const c = sevColor(s);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
      color: c, background: `color-mix(in srgb, ${c} 16%, transparent)`, padding: '2px 9px', borderRadius: 999, letterSpacing: '.03em',
    }}>{s || 'unknown'}</span>
  );
}

const STATUS_OPTS: { key: string; label: string }[] = [
  { key: 'acknowledged', label: 'Acknowledge' },
  { key: 'fixed', label: 'Mark fixed' },
  { key: 'wont_fix', label: "Won't fix" },
];

function ResolutionCard({ r, health, onStatus, onReanalyze }: {
  r: Resolution; health?: HealthApp; onStatus: (id: number, status: string) => void; onReanalyze: (r: Resolution) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const accent = sevColor(r.severity);
  const isLogic = r.targetType === 'logicapp';
  const done = r.status === 'fixed' || r.status === 'wont_fix';
  // For logic apps we know the live failure count (joined from health) — if 0,
  // this brief is from a past finding that isn't currently recurring.
  const currentlyHealthy = isLogic && health && (health.failed || 0) === 0;
  const doReanalyze = async () => { setBusy(true); try { await onReanalyze(r); } finally { setBusy(false); } };
  return (
    <div className="card" style={{ padding: 0, borderLeft: `3px solid ${accent}`, overflow: 'hidden', opacity: done ? 0.7 : 1 }}>
      <div style={{ padding: '14px 18px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <SeverityPill s={r.severity} />
              <span className="badge" style={{ fontSize: 10, background: 'rgba(255,255,255,.07)', color: 'var(--muted)' }}>
                {isLogic ? 'Logic App' : 'SWA'}
              </span>
              <span style={{ fontWeight: 700, fontSize: 14, wordBreak: 'break-word' }}>{r.targetName}</span>
              {r.status && r.status !== 'new' && (
                <span className="badge" style={{ fontSize: 10, background: done ? 'color-mix(in srgb, var(--good) 18%, transparent)' : 'rgba(255,255,255,.07)', color: done ? 'var(--good)' : 'var(--muted)' }}>
                  {r.status}
                </span>
              )}
              {currentlyHealthy && (
                <span className="badge" title="No failures in the current 2-day window — this brief is from a past finding"
                  style={{ fontSize: 10, background: 'color-mix(in srgb, var(--good) 16%, transparent)', color: 'var(--good)' }}>
                  ✓ currently healthy
                </span>
              )}
            </div>
            {r.title && <div className="muted" style={{ fontSize: 12.5, marginTop: 5, lineHeight: 1.4 }}>{r.title}</div>}
          </div>
          {r.portalLink && (
            <a href={r.portalLink} target="_blank" rel="noreferrer" className="icon-btn" title="Open in Azure Portal" style={{ flexShrink: 0, padding: 4 }}>
              <ExternalLink size={14} />
            </a>
          )}
        </div>

        {/* meta row */}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10, fontSize: 11.5, color: 'var(--muted)' }}>
          <span><strong style={{ color: 'var(--text, inherit)' }}>{r.errorCount ?? '—'}</strong> occurrences</span>
          <span>sources: {(r.sources || []).join(', ') || '—'}</span>
          {r.lastSeen && <span><Clock size={11} style={{ marginRight: 3 }} />last {fmtWhen(r.lastSeen)}</span>}
          {r.estimatedCost != null && <span>· ${Number(r.estimatedCost).toFixed(4)}</span>}
        </div>

        {/* logic-app health stats (joined from /api/logicapp-health) */}
        {isLogic && health && (
          <div style={{ display: 'flex', gap: 16, marginTop: 10, padding: '8px 12px', background: 'rgba(0,0,0,.18)', borderRadius: 8, fontSize: 12 }}>
            <span><Activity size={12} style={{ marginRight: 4, color: 'var(--accent)' }} /><strong>{health.runCount ?? '—'}</strong> runs (2d)</span>
            <span style={{ color: (health.failed || 0) > 0 ? 'var(--bad)' : undefined }}><strong>{health.failed ?? 0}</strong> failed</span>
            <span><Timer size={12} style={{ marginRight: 4 }} />max <strong>{fmtDur(health.maxDurationSec)}</strong></span>
            {health.schedule && <span className="muted">{health.schedule}</span>}
          </div>
        )}
      </div>

      {/* the resolution brief — prominent */}
      <div className="error-md" style={{ padding: '4px 18px 14px', fontSize: 13, lineHeight: 1.55, borderTop: '1px solid var(--border)' }}>
        {r.summaryMd
          ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{r.summaryMd}</ReactMarkdown>
          : <span className="muted">No resolution generated.</span>}
      </div>

      {/* review controls */}
      <div style={{ display: 'flex', gap: 6, padding: '8px 18px 12px', borderTop: '1px solid var(--border)', alignItems: 'center' }}>
        {STATUS_OPTS.map(o => (
          <button key={o.key} type="button" className="btn" onClick={() => onStatus(r.resolutionId, o.key)}
            disabled={r.status === o.key}
            style={{ fontSize: 11.5, padding: '4px 10px', opacity: r.status === o.key ? 0.5 : 1 }}>
            {o.key === 'fixed' && <CheckCircle2 size={12} style={{ marginRight: 4 }} />}{o.label}
          </button>
        ))}
        <button type="button" className="btn" onClick={doReanalyze} disabled={busy}
          title="Re-run the AI analysis for this target using the most recent failure data"
          style={{ fontSize: 11.5, padding: '4px 10px', marginLeft: 'auto', gap: 5 }}>
          <RefreshCw size={12} style={{ animation: busy ? 'pulse 1.2s ease-in-out infinite' : undefined }} />
          {busy ? 'Analyzing…' : 'Re-analyze'}
        </button>
      </div>
    </div>
  );
}

export default function ErrorResolution() {
  const [data, setData] = useState<Overview | null>(null);
  const [health, setHealth] = useState<Record<string, HealthApp>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sevFilter, setSevFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'swa' | 'logicapp'>('all');
  const [showResolved, setShowResolved] = useState(false);

  const load = useCallback((signal?: AbortSignal) => {
    setLoading(true); setError(null);
    getJson<Overview>('/api/error-resolution?mode=overview&days=14', { signal })
      .then((o) => { setData(o); setLoading(false); })
      .catch((e) => { if (e.name !== 'AbortError') { setError(e.message); setLoading(false); } });
    // best-effort health join (cached server-side)
    getJson<{ apps: HealthApp[] }>('/api/logicapp-health?mode=overview&days=2', { signal })
      .then((h) => { const m: Record<string, HealthApp> = {}; (h.apps || []).forEach(a => { m[a.name] = a; }); setHealth(m); })
      .catch(() => { /* non-fatal */ });
  }, []);

  useEffect(() => { const c = new AbortController(); load(c.signal); return () => c.abort(); }, [load]);

  const runDigest = async () => {
    setGenerating(true); setNotice(null); setError(null);
    try {
      // Incremental: only NEW failure signatures are re-analyzed; unchanged are skipped.
      const res = await getJson<any>('/api/error-resolution?mode=generate&days=2', { method: 'POST' });
      if (!res.ok) throw new Error(res.error || 'Generate failed (unknown error)');
      setNotice(`Digest complete — ${res.analyzed} new analyzed, ${res.skipped} unchanged (kept), of ${res.candidateCount} currently failing ($${res.totalCost}).`);
      load();
    } catch (e: any) {
      setError(e.message?.includes('admin') || e.message?.includes('Forbidden') ? 'Running the digest is admin-only.' : `Digest failed: ${e.message}`);
    } finally { setGenerating(false); }
  };

  // Manually send the digest email now (admin). force=1 bypasses the morning
  // gate; recipients come from ERROR_DIGEST_RECIPIENTS (default the team).
  const sendDigestNow = async () => {
    if (!window.confirm('Send the Error Resolution digest email now (to the configured recipients)?')) return;
    setGenerating(true); setNotice(null); setError(null);
    try {
      const res = await getJson<any>('/api/error-resolution?mode=digest&force=1', { method: 'POST' });
      setNotice(res.emailed
        ? `Digest emailed (${res.openIssues} open issue${res.openIssues === 1 ? '' : 's'}).`
        : `Digest ran (analyzed ${res.analyzed ?? 0}) but email was not sent${res.error ? `: ${res.error}` : ' (no open issues or email not configured)'}.`);
      load();
    } catch (e: any) {
      setError(e.message?.includes('admin') || e.message?.includes('Forbidden') ? 'Sending the digest is admin-only.' : `Send failed: ${e.message}`);
    } finally { setGenerating(false); }
  };

  // Per-target forced re-analysis with fresh data. If the target has no current
  // failures, the backend skips AI and reports healthy while keeping the brief.
  const reanalyze = async (r: Resolution) => {
    setNotice(null); setError(null);
    const url = `/api/error-resolution?mode=generate&days=2&type=${encodeURIComponent(r.targetType)}&name=${encodeURIComponent(r.targetName)}`;
    const res = await getJson<any>(url, { method: 'POST' });
    if (res.healthy) setNotice(res.message);
    else { setNotice(`Re-analyzed ${r.targetName} with fresh data.`); }
    load();
  };

  const setStatus = async (resolutionId: number, status: string) => {
    // optimistic
    setData(d => d ? { ...d, resolutions: d.resolutions.map(r => r.resolutionId === resolutionId ? { ...r, status } : r) } : d);
    try {
      await getJson('/api/error-resolution?mode=status', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ resolutionId, status }) });
    } catch (e: any) {
      setError(e.message?.includes('admin') || e.message?.includes('Forbidden') ? 'Changing status is admin-only.' : e.message);
      load(); // revert
    }
  };

  const resolvedCount = useMemo(() => (data?.resolutions || []).filter(r => r.status === 'fixed' || r.status === 'wont_fix').length, [data]);

  const visible = useMemo(() => {
    let list = data?.resolutions || [];
    if (!showResolved) list = list.filter(r => r.status !== 'fixed' && r.status !== 'wont_fix');
    if (typeFilter !== 'all') list = list.filter(r => r.targetType === typeFilter);
    if (sevFilter !== 'all') list = list.filter(r => r.severity === sevFilter);
    if (search.trim()) { const q = search.toLowerCase(); list = list.filter(r => r.targetName.toLowerCase().includes(q) || (r.title || '').toLowerCase().includes(q) || (r.summaryMd || '').toLowerCase().includes(q)); }
    return list;
  }, [data, typeFilter, sevFilter, search, showResolved]);

  const t = data?.totals;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Wrench size={20} style={{ color: 'var(--bad)' }} />
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>Error Resolution Agent</div>
          </div>
          <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
            AI resolution briefs across logic apps + apps{data && <> · last digest {data.resolutions[0]?.digestDate ? new Date(data.resolutions[0].digestDate).toLocaleDateString() : '—'}</>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={runDigest} disabled={generating} style={{ gap: 6, fontSize: 12.5 }} title="Re-analyze new failures (incremental) — no email">
            <Play size={14} style={{ animation: generating ? 'pulse 1.2s ease-in-out infinite' : undefined }} /> {generating ? 'Running…' : 'Run digest'}
          </button>
          <button className="btn" onClick={sendDigestNow} disabled={generating} style={{ gap: 6, fontSize: 12.5, borderColor: 'var(--good)', color: 'var(--good)' }} title="Send the digest email now to the configured recipients (admin)">
            <Mail size={14} /> Send email
          </button>
          <button className="btn" onClick={() => load()} disabled={loading || generating} style={{ gap: 6, fontSize: 12.5 }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {notice && <div className="card" style={{ padding: '10px 14px', borderLeft: '3px solid var(--good)', fontSize: 12.5, color: 'var(--good)' }}>{notice}</div>}
      {generating && <div className="muted" style={{ fontSize: 12 }}>Analyzing failures across all sources — this can take a minute or two.</div>}

      {/* KPIs */}
      {t && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[['targets', 'Targets', undefined], ['critical', 'Critical', '#ff4d4f'], ['high', 'High', 'var(--bad)'], ['medium', 'Medium', '#fbbf24'], ['open', 'Open', undefined]].map(([k, lbl, c]) => (
            <div key={k as string} className="card" style={{ padding: '12px 18px', minWidth: 96, textAlign: 'center', borderLeft: c ? `3px solid ${c}` : undefined }}>
              <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1, color: c as string }}>{t[k as string] ?? 0}</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{lbl as string}</div>
            </div>
          ))}
        </div>
      )}

      {/* controls */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search resolutions…"
            style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'inherit', fontSize: 13 }} />
        </div>
        <div style={{ display: 'flex', gap: 2, background: 'var(--border)', padding: 2, borderRadius: 8 }}>
          {([['all', 'All'], ['swa', 'SWA'], ['logicapp', 'Logic App']] as const).map(([k, lbl]) => (
            <button key={k} type="button" onClick={() => setTypeFilter(k)} style={{
              fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 6, cursor: 'pointer', border: 'none',
              background: typeFilter === k ? 'var(--panel, #11161f)' : 'transparent', color: typeFilter === k ? 'var(--accent)' : 'var(--muted)',
            }}>{lbl}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 2, background: 'var(--border)', padding: 2, borderRadius: 8 }}>
          {['all', ...SEV_ORDER].map(s => (
            <button key={s} type="button" onClick={() => setSevFilter(s)} style={{
              fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 6, cursor: 'pointer', border: 'none', textTransform: 'capitalize',
              background: sevFilter === s ? 'var(--panel, #11161f)' : 'transparent', color: sevFilter === s ? (s === 'all' ? 'var(--accent)' : sevColor(s)) : 'var(--muted)',
            }}>{s}</button>
          ))}
        </div>
        <button type="button" className="btn" onClick={() => setShowResolved(v => !v)}
          style={{ fontSize: 11.5, padding: '5px 11px', marginLeft: 'auto', opacity: resolvedCount === 0 ? 0.4 : 1 }}
          disabled={resolvedCount === 0}
          title={showResolved ? 'Hide resolved items' : 'Show resolved items'}>
          {showResolved ? 'Hide resolved' : `Show resolved${resolvedCount > 0 ? ` (${resolvedCount})` : ''}`}
        </button>
        <span className="muted" style={{ fontSize: 12 }}>{visible.length} of {(data?.resolutions.length ?? 0) - (showResolved ? 0 : resolvedCount)}</span>
      </div>

      {/* body */}
      {loading ? (
        <div className="muted" style={{ padding: '30px 0', textAlign: 'center' }}>Loading resolutions…</div>
      ) : error ? (
        <div className="card" style={{ padding: 20, borderLeft: '3px solid var(--bad)' }}>
          <div style={{ color: 'var(--bad)', fontWeight: 600, marginBottom: 6 }}><AlertTriangle size={15} style={{ marginRight: 6 }} />{error}</div>
        </div>
      ) : (data?.resolutions.length ?? 0) === 0 ? (
        <div className="card" style={{ padding: 28, textAlign: 'center' }}>
          <div className="muted" style={{ fontSize: 13 }}>No resolutions yet. Click <strong>Run digest</strong> to analyze current failures, or wait for the morning digest.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 16, alignItems: 'start' }}>
          {visible.map(r => <ResolutionCard key={r.resolutionId} r={r} health={r.targetType === 'logicapp' ? health[r.targetName] : undefined} onStatus={setStatus} onReanalyze={reanalyze} />)}
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.45} }
        .error-md h3 { font-size: 13px; font-weight: 700; margin: 12px 0 4px; color: var(--accent); }
        .error-md p { margin: 4px 0; }
        .error-md ul, .error-md ol { padding-left: 20px; margin: 4px 0; }
        .error-md li { margin: 2px 0; }
        .error-md code { background: rgba(255,255,255,.08); padding: 1px 5px; border-radius: 4px; font-size: 11.5px; }
        .error-md pre { background: rgba(0,0,0,.25); padding: 10px 12px; border-radius: 8px; overflow-x: auto; font-size: 11.5px; }
        .error-md pre code { background: none; padding: 0; }
        .error-md a { color: var(--accent); }
        .error-md strong { color: inherit; }
      `}</style>
    </div>
  );
}
